import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUser,
    faEnvelope,
    faLock,
    faPhone,
    faSignInAlt,
    faUserPlus
} from '@fortawesome/free-solid-svg-icons';
import './Membership.css';
import { AuthService, SettingsService } from '../../Services/Axios/Api';
import { toast, ToastContainer } from 'react-toastify';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import FacebookLogin from 'react-facebook-login/dist/facebook-login-render-props';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getToken, setDonorEmail } from '../../Services/Axios/axiosInstance';
import { useTranslation } from 'react-i18next';
import ReCAPTCHA from "react-google-recaptcha";

const Membership = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t, i18n } = useTranslation();

    const getPostAuthPath = () => {
        const currentPath = `${location.pathname}${location.search || ''}`;
        const isDonateFlow = location.pathname.startsWith('/donate') || location.pathname === '/general-donate';
        return isDonateFlow ? currentPath : '/donor-profile';
    };

    const [activeTab, setActiveTab] = useState('login');
    const [formData, setFormData] = useState({
        userName: '',
        email: '',
        phoneNumber: '',
        password: '',
        confirmPassword: ''
    });
    const [errors, setErrors] = useState({});
    const [apiError, setApiError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [recaptchaToken, setRecaptchaToken] = useState(null);
    const [publicConfig, setPublicConfig] = useState(null);
    const [isProcessingFacebookRedirect, setIsProcessingFacebookRedirect] = useState(false);

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const config = await SettingsService.getPublicConfig();
                setPublicConfig(config.result);
            } catch (e) {
                console.error(e);
            }
        };
        fetchConfig();
    }, []);

    const handleSocialLoginSuccess = (response) => {
        toast.success(t('Membership.LoginSuccess'));
        setDonorEmail(response.result?.email || response.result?.userName || formData.email);
        localStorage.setItem('isLoggedIn', 'true');
        window.dispatchEvent(new Event('authChange'));
        setTimeout(() => {
            navigate(getPostAuthPath());
        }, 2000);
    };

    const handleGoogleSuccess = async (credentialResponse) => {
        try {
            setIsLoading(true);
            const response = await AuthService.SocialLogin('Google', credentialResponse.credential);
            handleSocialLoginSuccess(response);
        } catch (error) {
            setApiError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFacebookResponse = async (response) => {
        if (response.accessToken) {
            try {
                setIsLoading(true);
                const apiResponse = await AuthService.SocialLogin('Facebook', response.accessToken);
                handleSocialLoginSuccess(apiResponse);
            } catch (error) {
                setApiError(error.message);
            } finally {
                setIsLoading(false);
            }
        }
    };

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');

        if (code && state === 'facebookdirect') {
            const processFacebookRedirect = async () => {
                setIsProcessingFacebookRedirect(true);
                try {
                    const clientId = process.env.FacebookClientId || process.env.REACT_APP_FacebookClientId || '1576381337397566';
                    const clientSecret = process.env.FacebookClientSecret || process.env.REACT_APP_FacebookClientSecret || '144d5e30da025246580b203813e5fefa';
                    const redirectUri = process.env.FacebookRedirectUrl || process.env.REACT_APP_FacebookRedirectUrl || 'https://abwabelkheir.com/membership';

                    const response = await fetch(`https://graph.facebook.com/v26.0/oauth/access_token?client_id=${clientId}&redirect_uri=${redirectUri}&client_secret=${clientSecret}&code=${code}`);
                    const data = await response.json();

                    if (data.access_token) {
                        await handleFacebookResponse({ accessToken: data.access_token });
                    } else if (data.error) {
                        setApiError(data.error.message || 'Facebook login failed.');
                    }
                } catch (error) {
                    console.error('Facebook redirect error:', error);
                    setApiError('Failed to process Facebook login.');
                } finally {
                    window.history.replaceState({}, document.title, window.location.pathname);
                    setIsProcessingFacebookRedirect(false);
                }
            };

            processFacebookRedirect();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = (isLogin = false) => {
        const newErrors = {};

        if (!isLogin) {
            if (!formData.userName.trim()) {
                newErrors.userName = t('Membership.Validation.UsernameRequired');
            }
            if (!formData.phoneNumber.trim()) {
                newErrors.phoneNumber = t('Membership.Validation.PhoneRequired');
            }
            if (formData.password !== formData.confirmPassword) {
                newErrors.confirmPassword = t('Membership.Validation.PasswordsDoNotMatch');
            }
        }

        if (!formData.email.trim()) {
            newErrors.email = t('Membership.Validation.EmailRequired');
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = t('Membership.Validation.EmailInvalid');
        }

        if (!formData.password) {
            newErrors.password = t('Membership.Validation.PasswordRequired');
        } else if (formData.password.length < 6) {
            newErrors.password = t('Membership.Validation.PasswordLength');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setApiError('');

        if (activeTab === 'register' && !recaptchaToken) {
            alert("Please verify you're not a robot");
            return;
        }

        if (!validateForm(activeTab === 'login')) {
            return;
        }

        setIsLoading(true);
        try {
            var response;
            if (activeTab === 'login') {
                response = await AuthService.Login(formData.email, formData.password);
                toast.success(t('Membership.LoginSuccess'));
                setDonorEmail(formData.email);
                localStorage.setItem('isLoggedIn', 'true');
                window.dispatchEvent(new Event('authChange'));
                setTimeout(() => {
                    navigate(getPostAuthPath());
                }, 2000);

            } else {
                response = await AuthService.Register(formData.userName, formData.email, formData.password, formData.confirmPassword, formData.phoneNumber);
                toast.success(t('Membership.RegisterSuccess'));
                setActiveTab('login');
            }

            if (!response.isSuccess) {
                setApiError(response.errorMessages?.[0] || t('Membership.RegisterFailed'));
                return;
            }


        } catch (error) {
            console.log(error);
            if (error) {
                setApiError(error.toString());
            } else {
                setApiError('An unexpected error occurred. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    if (isProcessingFacebookRedirect) {
        return (
            <Container className="MembershipContainer" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
                <Row className="justify-content-center mt-5 mb-5">
                    <Col md={8} lg={6}>
                        <Card className="membership-card pt-4 pb-4">
                            <Card.Body className="text-center py-5">
                                <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <h4 className="membership-title">
                                    {t('Membership.ProcessingFacebookLogin', 'Processing Facebook Login...')}
                                </h4>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        );
    }

    return (
        <Container className="MembershipContainer" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>

            <Row className="justify-content-center mt-5 mb-5">
                <Col md={8} lg={6}>
                    <Card className="membership-card pt-4 pb-4">
                        <Card.Body>
                            <div className="text-center mb-4">
                                <h2 className="membership-title">
                                    {activeTab === 'login' ? t('Membership.WelcomeBack') : t('Membership.CreateAccount')}
                                </h2>
                                <p className="membership-subtitle">
                                    {activeTab === 'login'
                                        ? t('Membership.SignInToContinue')
                                        : t('Membership.JoinUs')}
                                </p>
                                {(location.pathname.startsWith('/donate') || location.pathname === '/general-donate') && (
                                    <Alert variant="info" className="mt-3 mb-0">
                                        {t('Membership.LoginToDonate', 'Please login or create an account to continue with your donation.')}
                                    </Alert>
                                )}
                            </div>

                            {apiError && (
                                <Alert variant="danger" className="mb-4">
                                    {apiError}
                                </Alert>
                            )}

                            {publicConfig && (publicConfig.googleClientId || publicConfig.facebookAppId) && (
                                <div className="social-logins mb-4 text-center">
                                    <p className="text-muted mb-3">{t('Membership.OrLoginWith', 'Or login with')}</p>
                                    <div className="d-flex justify-content-center align-items-center gap-3">
                                        {publicConfig.googleClientId && (
                                            <GoogleOAuthProvider clientId={publicConfig.googleClientId}>
                                                <GoogleLogin
                                                    onSuccess={handleGoogleSuccess}
                                                    onError={() => setApiError('Google Login Failed')}
                                                    use_fedcm_for_prompt={true}
                                                />
                                            </GoogleOAuthProvider>
                                        )}
                                        {publicConfig.facebookAppId && (
                                            <FacebookLogin
                                                appId={publicConfig.facebookAppId}
                                                autoLoad={false}
                                                fields="name,email,picture"
                                                callback={handleFacebookResponse}
                                                render={renderProps => (
                                                    <Button variant="outline-primary" onClick={renderProps.onClick} className="d-flex align-items-center">
                                                        Facebook
                                                    </Button>
                                                )}
                                            />
                                        )}
                                    </div>
                                    <hr className="my-4" />
                                </div>
                            )}

                            <div className="tab-buttons mb-4">
                                <Button
                                    variant={activeTab === 'login' ? 'primary' : 'outline-primary'}
                                    className="tab-button"
                                    onClick={() => setActiveTab('login')}
                                >
                                    <FontAwesomeIcon icon={faSignInAlt} className="me-2" /> {t('Membership.Login')}
                                </Button>
                                <Button
                                    variant={activeTab === 'register' ? 'primary' : 'outline-primary'}
                                    className="tab-button"
                                    onClick={() => setActiveTab('register')}
                                >
                                    <FontAwesomeIcon icon={faUserPlus} className="me-2" /> {t('Membership.Register')}
                                </Button>
                            </div>

                            <Form onSubmit={handleSubmit}>
                                {activeTab === 'register' && (
                                    <>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                <FontAwesomeIcon icon={faUser} className="me-2" />
                                                {t('Membership.Username')}
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="userName"
                                                value={formData.userName}
                                                onChange={handleInputChange}
                                                isInvalid={!!errors.userName}
                                                placeholder={t('Membership.EnterUsername')}
                                                disabled={isLoading}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.userName}
                                            </Form.Control.Feedback>
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                <FontAwesomeIcon icon={faPhone} className="me-2" />
                                                {t('Membership.PhoneNumber')}
                                            </Form.Label>
                                            <Form.Control
                                                type="tel"
                                                name="phoneNumber"
                                                value={formData.phoneNumber}
                                                onChange={handleInputChange}
                                                isInvalid={!!errors.phoneNumber}
                                                placeholder={t('Membership.EnterPhoneNumber')}
                                                disabled={isLoading}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.phoneNumber}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </>
                                )}

                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        <FontAwesomeIcon icon={faEnvelope} className="me-2" /> {t('Membership.Email')}
                                    </Form.Label>
                                    <Form.Control
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        isInvalid={!!errors.email}
                                        placeholder={t('Membership.EnterEmail')}
                                        disabled={isLoading}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.email}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        <FontAwesomeIcon icon={faLock} className="me-2" /> {t('Membership.Password')}
                                    </Form.Label>
                                    <Form.Control
                                        type="password"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        isInvalid={!!errors.password}
                                        placeholder={t('Membership.EnterPassword')}
                                        disabled={isLoading}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.password}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                {activeTab === 'register' && (
                                    <Form.Group className="mb-4">
                                        <Form.Label>
                                            <FontAwesomeIcon icon={faLock} className="me-2" /> {t('Membership.ConfirmPassword')}
                                        </Form.Label>
                                        <Form.Control
                                            type="password"
                                            name="confirmPassword"
                                            value={formData.confirmPassword}
                                            onChange={handleInputChange}
                                            isInvalid={!!errors.confirmPassword}
                                            placeholder={t('Membership.EnterConfirmPassword')}
                                            disabled={isLoading}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.confirmPassword}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                )}
                                <div className="d-flex justify-content-between mb-3">
                                    <Link to="/forgotpassword" className="forgot-password-link">{t('Membership.ForgotPassword')}</Link>
                                </div>

                                {activeTab === 'register' && (
                                    <div className="d-flex justify-content-center mb-3">
                                        <ReCAPTCHA
                                            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LeUyq4sAAAAAED6EEYKWhqPNMVhimC1mSNqRN9t"}
                                            onChange={(value) => setRecaptchaToken(value)}
                                        />
                                    </div>
                                )}
                                <Button
                                    variant="primary"
                                    type="submit"
                                    className="w-100 submit-button"
                                    disabled={isLoading}
                                >
                                    {isLoading ? (
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                    ) : null}
                                    {activeTab === 'login' ? t('Membership.SignIn') : t('Membership.CreateAccount')}
                                </Button>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default Membership;
